import React, { useCallback, useMemo } from 'react';

import { Button, FontAwesomeIcon, Input } from '@appcharge/shared-ui';
import { EToasterStatus, showToast } from 'utils/showToast';

import { cn } from 'components/lib/utils';

import { toastMessages } from '../DeveloperSettings.constants';
import { IVersionConfig } from '../DeveloperSettings.types';

interface IVersionRowProps {
  config: IVersionConfig;
  disabled?: boolean;
  isLatest: boolean;
  onTestClick?: () => void;
  testButtonDisabled?: boolean;
  shouldShowRadioButton?: boolean;
}

export const VersionRow: React.FC<IVersionRowProps> = ({
  config,
  disabled,
  isLatest,
  onTestClick,
  testButtonDisabled,
  shouldShowRadioButton
}) => {
  const hasValue = useMemo(() => Boolean(config.value), [config.value]);

  const handleCopyUrl = useCallback((): void => {
    if (!hasValue) {
      return;
    }
    navigator.clipboard.writeText(config.value);
    showToast({
      message: toastMessages.urlCopied,
      status: EToasterStatus.SUCCESS
    });
  }, [config.value, hasValue]);

  const handleOpenDocs = useCallback((): void => {
    if (config.docsUrl) {
      window.open(config.docsUrl, '_blank');
    }
  }, [config.docsUrl]);

  const handleRadioClick = useCallback((): void => {
    if (config.onSelectVersion && !disabled) {
      config.onSelectVersion();
    }
  }, [config, disabled]);

  const copyIconStyles = useMemo(
    () =>
      cn(
        'text-base-mutedForeground',
        hasValue ? 'cursor-pointer' : 'cursor-not-allowed opacity-50'
      ),
    [hasValue]
  );

  return (
    <div className="flex items-start gap-4 flex-wrap">
      <div className="flex flex-col gap-2 w-[80px]">
        <label className="text-sm font-medium text-base-foreground">
          Version
        </label>
        <span className="flex h-10 gap-2 items-center text-sm font-normal text-base-foreground">
          {shouldShowRadioButton && (
            <Input
              type="radio"
              id="activeVersion"
              name="activeVersion"
              checked={config.isSelected}
              onChange={handleRadioClick}
              disabled={disabled}
              className="w-4 cursor-pointer accent-indigo-600 disabled:cursor-not-allowed disabled:opacity-50"
            />
          )}
          <span>{config.label}</span>
        </span>
      </div>
      <div className="flex flex-col gap-2">
        <label className="text-sm font-medium text-base-foreground">URL</label>
        <div className="flex items-center gap-3">
          <Input
            className="w-[456px]"
            value={config.value}
            onChange={(e) => config.onChange(e.target.value)}
            placeholder="https://"
            disabled={disabled}
            state={config.error ? 'error' : 'default'}
            rightIcon={
              <FontAwesomeIcon
                icon="fa-regular fa-copy"
                onClick={handleCopyUrl}
              />
            }
            rightIconStyles={copyIconStyles}
          />
        </div>
        {config.error && (
          <p className="mt-1 text-xs text-red-600">{config.error}</p>
        )}
      </div>
      {config.afterUrlField}
      {isLatest && onTestClick && (
        <Button
          variant="secondary"
          onClick={onTestClick}
          disabled={testButtonDisabled}
          className="shrink-0 self-end bg-base-secondary font-normal shadow-sm"
        >
          Test
          <FontAwesomeIcon
            icon="fa-solid fa-code"
            className="ml-1 font-normal"
          />
        </Button>
      )}
      {config.docsUrl && (
        <Button
          variant="ghost"
          onClick={handleOpenDocs}
          className="shrink-0 self-end shadow-none"
        >
          Docs
          <FontAwesomeIcon icon="fa-regular fa-file-lines" className="ml-1" />
        </Button>
      )}
    </div>
  );
};
